import axios from 'axios'
import Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify';
import { normalizeSearch } from '../utils/search'

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Nama status yang dipakai UI tidak sama dengan yang dikenali endpoint untuk
// dua status pertama. Endpoint mengabaikan nilai yang tidak dikenal secara
// diam-diam -- membalas 200 dengan seluruh data seolah-olah itu hasil filter --
// jadi terjemahannya dikunci di satu tempat ini, dilewati semua pemanggil.
const REGISTRATION_STATUS_PARAM = {
  'Belum verifikasi': 'Menunggu Verifikasi',
  'Verif oleh Korwil': 'Menunggu Persetujuan',
  Disetujui: 'Disetujui',
  Ditolak: 'Ditolak',
}

const toStatusParam = (status) => {
  if (!status || status === 'All') {
    return undefined
  }
  const mapped = REGISTRATION_STATUS_PARAM[status]
  if (!mapped) {
    // Status baru yang belum dipetakan akan balik tanpa filter tanpa error apa
    // pun, persis bug yang bikin tab Verif oleh Korwil menampilkan semua data.
    console.warn(
      `[registrations] Status "${status}" belum ada di REGISTRATION_STATUS_PARAM; server kemungkinan mengabaikannya.`,
    )
    return status
  }
  return mapped
}

export const getAllRegistrations = async ({
  page = 1,
  pageSize = 10,
  status = 'All',
  korwil = '',
  search,
  signal,
} = {}) => {
  try {
    const accessToken = Cookies.get('access_token')
    // Semua filter di-AND oleh server. Field yang kosong sengaja tidak dikirim
    // sama sekali: `&fullname=` diabaikan server tapi bikin cache key berbeda
    // untuk hasil yang sama.
    const { fullname, nik, email } = normalizeSearch(search)
    // `uuid` sengaja tidak dikirim: server tidak pernah membacanya.
    const response = await axios.get(`${API_URL}/api/registrations`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: {
        page,
        pageSize,
        status: toStatusParam(status),
        korwil: korwil || undefined,
        fullname: fullname || undefined,
        nik: nik || undefined,
        email: email || undefined,
      },
      signal,
    })
    return response.data
  } catch (error) {
    // Pembatalan oleh user bukan error yang perlu diributkan di console.
    if (!axios.isCancel(error)) {
      console.error('Error fetching registration data:', error)
    }
    throw error
  }
}

export const getRegistrationFile = async (id) => {
  try {
    const accessToken = Cookies.get('access_token')
    const uuid = Cookies.get('uuid')
    const response = await axios.get(
      `${API_URL}/api/registration/${id}/file?uuid=${uuid}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    throw error
  }
}

export const getAllActiveMembers = async ({
  query,
  pageSize,
  page,
  status,
  korwil,
  sortColumnn,
  sortOrder,
  valid_at,
  signal,
}) => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.get(`${API_URL}/api/active-members`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      params: {
        query,
        pageSize,
        page,
        status,
        korwil,
        sortColumnn,
        sortOrder
      },
      signal,
    })
    return response.data
  } catch (error) {
    // Pembatalan oleh user bukan error yang perlu diributkan di console.
    if (!axios.isCancel(error)) {
      console.error('Error fetching active members data:', error)
    }
    throw error
  }
}

export const HetPrintNewCard = async (id) => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.post(
      `${API_URL}/api/member-card/${id}/print`,
      undefined,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )
    toast.success('Permintaan cetak kartu berhasil diproses.');
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    toast.error('Terjadi kesalahan saat memproses permintaan cetak kartu.');
    throw error
  }
}

export const getListUserBydateStartFilter = async () => {
  try {
    const accessToken = Cookies.get('access_token')
    const uuid = Cookies.get('uuid')
    const response = await axios.get(`${API_URL}/api/korwils/me?uuid=${uuid}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    toast.error('Gagal memuat daftar pengguna berdasarkan tanggal.');
    throw error
  }
}



export const postRegistrations = async (form) => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.post(`${API_URL}/api/registration`, form, {
      headers: { Authorization: `Bearer ${accessToken}` },
      'Content-Type': 'multipart/form-data',
    })
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    throw error
  }
}

// Daftar korwil sama untuk semua <KorwilSelect> di halaman, jadi request-nya
// dishare: instance kedua dan seterusnya ikut menunggu promise yang sama.
// Cache dibuang lagi kalau request gagal supaya masih bisa dicoba ulang.
let korwilOptionsPromise = null

export const getKorwilOptions = async () => {
  if (!korwilOptionsPromise) {
    const accessToken = Cookies.get('access_token')
    const uuid = Cookies.get('uuid')
    korwilOptionsPromise = axios
      .get(`${API_URL}/api/korwils/me?uuid=${uuid}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => response.data)
      .catch((error) => {
        korwilOptionsPromise = null
        console.error('Error fetching korwil options:', error)
        throw error
      })
  }
  return korwilOptionsPromise
}

export const postVerifyRegistration = async (id) => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.post(
      `${API_URL}/api/registration/${id}/verify`,
      undefined,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    throw error
  }
}

export const postApproveRegistration = async (id) => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.post(
      `${API_URL}/api/registration/${id}/approve`,
      undefined,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )
    Cookies.set('uuid', uuidv4(), {
      path: '/',
      domain: window.location.hostname,
      expires: 1,
    })
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    throw error
  }
}

export const postUnverifyRegistration = async (id) => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.post(
      `${API_URL}/api/registration/${id}/unverify`,
      undefined,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    throw error
  }
}

export const postUnpproveRegistration = async (id) => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.post(
      `${API_URL}/api/registration/${id}/unapprove`,
      undefined,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    throw error
  }
}

export const deleteRegistration = async (id) => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.delete(`${API_URL}/api/registration/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    throw error
  }
}

export const getShirtSizeOptions = async () => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.get(`${API_URL}/api/general-infos`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (response.data?.data && response.data?.data.length > 0) {
      return response?.data.data[0].attributes.shirtSizeOption ?? []
    }
    return []
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    throw error
  }
}

export const postRejectRegistration = async (id) => {
  try {
    const accessToken = Cookies.get('access_token')
    const response = await axios.post(
      `${API_URL}/api/registration/${id}/reject`,
      undefined,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )
    return response.data
  } catch (error) {
    console.error('Error fetching invoice data:', error)
    throw error
  }
}

export const getKorwilByLocation = async ({
  subdistrict_code,
  district_code,
  city_code,
  province_code,
}) => {
  const accessToken = Cookies.get('access_token')
  const response = await axios.get(`${API_URL}/api/korwils/location`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    params: {
      subdistrictCode: subdistrict_code,
      districtCode: district_code,
      cityCode: city_code,
      provinceCode: province_code,
    },
  })

  return response.data?.data
}

export const fetchActiveMembers = async ({
  query = '',
  pageSize = 10,
  page = 1,
  status = 'All',
  korwil = '',
  valid_at = '',
  memberCard = 'Belum ada',
}) => {
  try {
    const accessToken = Cookies.get('access_token');
    const response = await axios.get(
      `${API_URL}/api/active-members/print`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          query,
          pageSize,
          page,
          status,
          korwil,
          valid_at,
          memberCard,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching active members:', error);
    throw error;
  }
};

export const printAllActiveMembers = async (startDate, endDate) => {
  if (!startDate || !endDate) {
    toast.error('Please select both start and end dates.');
    return;
  }

  try {
    const accessToken = Cookies.get('access_token');
    await axios.post(
      `${API_URL}/api/active-member/proses-memberCard`,
      {
      ActiveAt: new Date(startDate),
      UntilAt: new Date(endDate),
      },
      {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      }
    );

    toast.success('Data has been successfully sent to your email.');
  } catch (error) {
    console.error('Error printing all data:', error);
    toast.error('Failed to process the request. Please try again later.');
  }
};

export const getActiveMembersForCSV = async (validAt, pageSize = 1000, page = 1) => {
  try {
    const accessToken = Cookies.get('access_token');
    const response = await axios.get(
      `${API_URL}/api/active-members/print`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: {
          valid_at: validAt,
          pageSize,
          page,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching active members for CSV:', error);
    toast.error('Failed to fetch data for CSV export.');
    throw error;
  }
};
